<template>
  <div>
    <Card class="country-general risques-locaux-card">
      <div class="general-header">
        <div style="display: flex; align-items: center;">
          <Icon
            :type="collapsed ? 'ios-arrow-forward' : 'ios-arrow-down'"
            :size="20"
            class="pointer-cursor"
            style="margin-right: 10px"
            @click.native="collapsed = !collapsed"
          />
          <Icon
            type="md-warning"
            :size="25"
            style="margin-right: 15px"
          />
          <strong>{{ $t('Risques locaux') }}</strong>
        </div>
        <div style="display: flex; align-items: center; gap: 10px;">
          <Icon
            v-if="(s2ec.user.is_admin || s2ec.user.is_superuser) && !editMode"
            type="md-create"
            size="20"
            class="pointer-cursor"
            @click.native="toggleEditMode"
          />
          <Icon
            v-if="editMode && (s2ec.user.is_admin || s2ec.user.is_superuser)"
            type="md-close"
            size="20"
            class="pointer-cursor"
            @click.native="cancelEdit"
          />
        </div>
      </div>

      <div v-if="!collapsed && editMode">
        <div v-if="editableItems.length > 0">
          <Table
            :columns="editTableColumns"
            :data="editableItems"
            :show-header="true"
            stripe
          />
        </div>
        <div v-else class="empty-fact-type">
          <h4 class="text-center">{{ $t('No items to edit') }}</h4>
        </div>

        <div class="risques-locaux-actions">
          <Button
            type="default"
            @click="cancelEdit"
          >
            {{ $t('Cancel') }}
          </Button>
          <Button
            type="primary"
            :loading="loading"
            @click="saveItems"
          >
            {{ $t('Save') }}
          </Button>
        </div>
      </div>

      <div v-else-if="!collapsed && !editMode">
        <Table
          v-if="sousSections.length > 0"
          :columns="tableColumns"
          :data="sousSections"
          :show-header="true"
          stripe
        />

        <div
          v-else
          class="empty-fact-type"
        >
          <h4 class="text-center">
            {{ $t('No data') }}
          </h4>
        </div>
      </div>

      <Spin
        v-if="loading"
        fix
      />
    </Card>

    <!-- Markdown Editor Modal -->
    <Modal
      v-model="editorModalVisible"
      :title="$t('Edit Content')"
      width="900"
      :mask-closable="false"
      @on-ok="saveEditorModal"
      @on-cancel="cancelEditorModal"
    >
      <AppMarkdownEditor
        v-if="editorModalVisible"
        v-model="currentEditingContent"
        :editable="true"
        min-height="500px"
      />
    </Modal>
  </div>
</template>

<script>
import { getCountryContentSectionQry, updateCountryContentSectionQry } from "../../../services/countryInfo.service";
import AppMarkdownEditor from '@/components/common/AppMarkdownEditor';
import { marked } from 'marked';

export default {
    name: "RisquesLocaux",
    components: {
        AppMarkdownEditor
    },
    data() {
        return {
            loading: false,
            editMode: false,
            collapsed: true,
            sectionData: null,
            sousSections: [],
            editableItems: [],
            editorModalVisible: false,
            currentEditingContent: '',
            currentEditingIndex: null,
        }
    },
    created() {
        this.loadRisquesLocaux();
    },
    computed: {
        tableColumns() {
            return [
                {
                    title: this.$t('Title'),
                    key: 'titre',
                    width: 250,
                },
                {
                    title: this.$t('Content'),
                    key: 'contenu_md',
                    render: (h, params) => {
                        return h('div', {
                            domProps: {
                                innerHTML: this.renderMarkdown(params.row.contenu_md)
                            }
                        });
                    }
                }
            ];
        },
        editTableColumns() {
            return [
                {
                    title: this.$t('Title'),
                    key: 'titre',
                    width: 250,
                    render: (h, params) => {
                        return h('Input', {
                            props: {
                                value: params.row.titre,
                                placeholder: this.$t('Title')
                            },
                            on: {
                                'on-change': (event) => {
                                    this.$set(this.editableItems[params.index], 'titre', event.target.value);
                                },
                                input: (value) => {
                                    this.$set(this.editableItems[params.index], 'titre', value);
                                }
                            }
                        });
                    }
                },
                {
                    title: this.$t('Content'),
                    key: 'contenu_md',
                    render: (h, params) => {
                        return h('div', [
                            h('div', {
                                domProps: {
                                    innerHTML: this.renderMarkdown(params.row.contenu_md || '')
                                },
                                style: {
                                    maxHeight: '100px',
                                    overflow: 'auto',
                                    marginBottom: '8px',
                                    padding: '8px',
                                    background: '#f8f8f9',
                                    borderRadius: '4px',
                                    minHeight: '40px'
                                }
                            }),
                            h('Button', {
                                props: {
                                    size: 'small',
                                    type: 'primary',
                                    icon: 'md-create'
                                },
                                on: {
                                    click: () => {
                                        this.openEditorModal(params.index);
                                    }
                                }
                            }, this.$t('Edit Content'))
                        ]);
                    }
                }
            ];
        }
    },
    methods: {
        loadRisquesLocaux() {
            this.loading = true;
            getCountryContentSectionQry(this.$route.params.country.toUpperCase(), 'Risques locaux')
                .then(res => {
                    this.sectionData = res.data;
                    this.sousSections = (res.data.sous_sections || []).sort((a, b) => a.ordre - b.ordre);
                })
                .catch(() => {
                    this.$Message.error({
                        content: this.$t('Unable to retrieve risques locaux'),
                        duration: 5
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        toggleEditMode() {
            this.editMode = true;

            // Clone the items for editing
            try {
                if (this.$lodash && this.$lodash.cloneDeep) {
                    this.editableItems = this.$lodash.cloneDeep(this.sousSections);
                } else {
                    this.editableItems = JSON.parse(JSON.stringify(this.sousSections));
                }
            } catch (e) {
                this.editableItems = [...this.sousSections];
            }
        },

        cancelEdit() {
            this.editMode = false;
            this.editableItems = [];
        },

        saveItems() {
            this.loading = true;

            // Prepare the data to send - wrap subsections in the correct format
            const dataToSend = {
                sous_sections: this.editableItems.map(item => ({
                    id: item.id,
                    titre: item.titre,
                    contenu_md: item.contenu_md,
                    ordre: item.ordre,
                    publie: item.publie
                }))
            };

            console.log('Sending data:', dataToSend);

            updateCountryContentSectionQry(this.$route.params.country.toUpperCase(), 'Risques locaux', dataToSend)
                .then(() => {
                    this.$Message.success({
                        content: this.$t('Successfully updated risques locaux'),
                        duration: 3
                    });
                    // Reload the data
                    this.loadRisquesLocaux();
                    // Exit edit mode
                    this.cancelEdit();
                })
                .catch((error) => {
                    console.error('Error updating risques locaux:', error);
                    this.$Message.error({
                        content: this.$t('Error updating risques locaux'),
                        duration: 5
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        openEditorModal(index) {
            this.currentEditingIndex = index;
            this.currentEditingContent = this.editableItems[index].contenu_md || '';
            this.editorModalVisible = true;
        },

        saveEditorModal() {
            if (this.currentEditingIndex !== null) {
                this.$set(this.editableItems[this.currentEditingIndex], 'contenu_md', this.currentEditingContent);
            }
            this.cancelEditorModal();
        },

        cancelEditorModal() {
            this.editorModalVisible = false;
            this.currentEditingContent = '';
            this.currentEditingIndex = null;
        },

        renderMarkdown(markdown) {
            if (!markdown) return '';
            try {
                marked.setOptions({
                    breaks: true,
                    gfm: true
                });
                return marked(markdown);
            } catch (error) {
                console.error('Markdown rendering error:', error);
                return markdown;
            }
        }
    }
}
</script>

<style>
.risques-locaux-card {
  margin-top: 20px;
}

.general-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e8eaec;
}

.risques-locaux-actions {
  margin-top: 20px;
  text-align: right;
}

.risques-locaux-actions button {
  margin-left: 10px;
}

.empty-fact-type {
  padding: 40px 20px;
}

.pointer-cursor {
  cursor: pointer;
}

/* Table content styling */
.risques-locaux-card .ivu-table td {
  vertical-align: top;
}

.risques-locaux-card .ivu-table p {
  margin: 0.5em 0;
}

.risques-locaux-card .ivu-table p:first-child {
  margin-top: 0;
}

.risques-locaux-card .ivu-table p:last-child {
  margin-bottom: 0;
}

.risques-locaux-card .ivu-table strong {
  font-weight: 600;
}

.risques-locaux-card .ivu-table a {
  color: #2d8cf0;
  text-decoration: none;
}

.risques-locaux-card .ivu-table a:hover {
  text-decoration: underline;
}

/* Edit mode table styling */
.risques-locaux-card .ivu-table .ivu-input-wrapper,
.risques-locaux-card .ivu-table .ivu-input {
  width: 100%;
}

.risques-locaux-card .ivu-table textarea.ivu-input {
  min-height: 80px;
}
</style>
